import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { fetchMenu, submitOrder } from "@/lib/api";

type ApiMenuItem = { id: number; name: string; price: string };
type ApiCategory = { id: number; name: string; items: ApiMenuItem[] };

const orderSchema = z.object({
  name: z.string().min(2, "Add your name."),
  email: z.string().email("Enter a valid email."),
  phone: z
    .string()
    .min(10, "Share your phone number.")
    .max(20, "Phone number too long."),
  item: z.string().min(1, "Pick something from the menu."),
  pickupDate: z.string().min(1, "When should it be ready?"),
  notes: z
    .string()
    .max(280, "Keep notes under 280 characters.")
    .optional()
    .or(z.literal("")),
});

type OrderForm = z.infer<typeof orderSchema>;
type OrderStatus = { type: "success" | "error"; message: string; detail?: string };

export function PlanPickup() {
  const [menuItems, setMenuItems] = useState<(ApiMenuItem & { categoryName: string })[]>([]);
  const [menuLoading, setMenuLoading] = useState(true);
  const [orderStatus, setOrderStatus] = useState<OrderStatus | null>(null);

  useEffect(() => {
    fetchMenu()
      .then((categories: ApiCategory[]) => {
        const items = categories.flatMap((cat) =>
          cat.items.map((item) => ({ ...item, categoryName: cat.name }))
        );
        setMenuItems(items);
      })
      .catch(() => setMenuItems([]))
      .finally(() => setMenuLoading(false));
  }, []);

  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<OrderForm>({
    resolver: zodResolver(orderSchema),
    defaultValues: {
      name: "",
      email: "",
      phone: "",
      item: "",
      pickupDate: "",
      notes: "",
    },
  });

  const onSubmit = async (data: OrderForm) => {
    setOrderStatus(null);

    const selected = menuItems.find((m) => m.name === data.item);

    try {
      await submitOrder({
        customer_name: data.name,
        customer_email: data.email,
        customer_phone: data.phone,
        menu_item_id: selected?.id ?? null,
        item_name: data.item,
        pickup_date: data.pickupDate,
        notes: data.notes ?? "",
      });

      const readableDate = data.pickupDate
        ? new Date(`${data.pickupDate}T00:00:00`).toLocaleDateString("en-US", {
            month: "long",
            day: "numeric",
          })
        : "your chosen date";

      setOrderStatus({
        type: "success",
        message: `We received your request for ${data.item}.`,
        detail: `Expect a text to ${data.phone} confirming ${readableDate}.`,
      });

      reset({ name: "", email: "", phone: "", item: "", pickupDate: "", notes: "" });
    } catch {
      setOrderStatus({
        type: "error",
        message: "Something went wrong submitting your order.",
        detail: "Please try again or contact us directly.",
      });
    }
  };

  const inputClass =
    "bg-input/50 border-white/10 text-foreground placeholder:text-muted-foreground/50 focus:border-primary focus:ring-primary/20";

  return (
    <section className="mx-auto max-w-3xl px-6 py-20">
      <Card className="bg-card border-white/5 shadow-2xl overflow-hidden relative">
        <div className="absolute top-0 right-0 p-32 bg-primary/5 blur-3xl rounded-full pointer-events-none" />
        <CardHeader className="p-10 pb-2 border-b border-white/5">
          <CardTitle className="font-serif text-4xl text-foreground">
            Plan a Pickup
          </CardTitle>
          <CardDescription className="text-base text-muted-foreground mt-2">
            Share a few details and we'll text you to confirm your order and
            pickup time.
          </CardDescription>
        </CardHeader>
        <CardContent className="p-10 pt-8">
          <form className="space-y-8" onSubmit={handleSubmit(onSubmit)}>
            <div className="grid gap-6 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="name" className="text-foreground/80">
                  Full name
                </Label>
                <Input
                  id="name"
                  placeholder="Your name"
                  className={inputClass}
                  aria-invalid={!!errors.name}
                  {...register("name")}
                />
                {errors.name && (
                  <p className="text-xs text-red-400">{errors.name.message}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="email" className="text-foreground/80">
                  Email
                </Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@email.com"
                  className={inputClass}
                  aria-invalid={!!errors.email}
                  {...register("email")}
                />
                {errors.email && (
                  <p className="text-xs text-red-400">{errors.email.message}</p>
                )}
              </div>
            </div>
            <div className="grid gap-6 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="phone" className="text-foreground/80">
                  Phone
                </Label>
                <Input
                  id="phone"
                  placeholder="(555) 123-4567"
                  className={inputClass}
                  aria-invalid={!!errors.phone}
                  {...register("phone")}
                />
                {errors.phone && (
                  <p className="text-xs text-red-400">{errors.phone.message}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="pickupDate" className="text-foreground/80">
                  Preferred pickup date
                </Label>
                <Input
                  id="pickupDate"
                  type="date"
                  className={`${inputClass} text-white`}
                  style={{ colorScheme: "dark" }}
                  aria-invalid={!!errors.pickupDate}
                  {...register("pickupDate")}
                />
                {errors.pickupDate && (
                  <p className="text-xs text-red-400">
                    {errors.pickupDate.message}
                  </p>
                )}
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="item" className="text-foreground/80">
                Item
              </Label>
              {menuLoading ? (
                <p className="text-sm text-muted-foreground">Loading menu...</p>
              ) : (
                <Controller
                  control={control}
                  name="item"
                  render={({ field }) => (
                    <Select
                      onValueChange={(value) => {
                        field.onChange(value);
                        field.onBlur();
                      }}
                      value={field.value}
                    >
                      <SelectTrigger
                        id="item"
                        className="bg-input/50 border-white/10 text-foreground focus:border-primary focus:ring-primary/20"
                      >
                        <SelectValue placeholder="Select a menu item" />
                      </SelectTrigger>
                      <SelectContent className="bg-popover border-white/10 text-popover-foreground">
                        {menuItems.map((item) => (
                          <SelectItem
                            key={item.id}
                            value={item.name}
                            className="focus:bg-primary/20 focus:text-primary-foreground"
                          >
                            {item.name} — {item.price}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
              )}
              {errors.item && (
                <p className="text-xs text-red-400">{errors.item.message}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="notes" className="text-foreground/80">
                Notes (allergies, special requests)
              </Label>
              <textarea
                id="notes"
                rows={4}
                aria-invalid={!!errors.notes}
                className="w-full rounded-md border border-white/10 bg-input/50 p-3 text-sm text-foreground shadow-sm placeholder:text-muted-foreground/50 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/20"
                placeholder="Any special requests or dietary needs..."
                {...register("notes")}
              />
              {errors.notes && (
                <p className="text-xs text-red-400">{errors.notes.message}</p>
              )}
            </div>
            <Button
              type="submit"
              className="w-full bg-primary text-primary-foreground hover:bg-primary/90 h-12 text-base font-semibold"
              disabled={isSubmitting}
            >
              {isSubmitting ? "Sending..." : "Send Order Request"}
            </Button>
          </form>
          {orderStatus && (
            <div
              className={`mt-8 flex items-start gap-4 rounded-xl border p-6 ${
                orderStatus.type === "success"
                  ? "border-primary/20 bg-primary/10 text-primary"
                  : "border-red-400/20 bg-red-400/10 text-red-400"
              }`}
            >
              <CheckCircle2 className="mt-1 size-5 shrink-0" />
              <div>
                <p className="font-serif text-lg font-medium">
                  {orderStatus.message}
                </p>
                <p className={`text-sm mt-1 ${
                  orderStatus.type === "success" ? "text-primary/80" : "text-red-400/80"
                }`}>
                  {orderStatus.detail}
                </p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </section>
  );
}
